import type { CreatedInviteDTO, MemberDTO } from '@kartothek/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { t, tDyn } from '../i18n';
import { CopyField } from './CopyField';

const ROLES: MemberDTO['role'][] = ['dm', 'co-dm', 'player'];

/** Mitglieder und Einladungslinks einer Kampagne (nur für die Rolle DM). */
export function MembersSettings({ cid, myId }: { cid: string; myId: string }) {
  const qc = useQueryClient();
  const members = useQuery({ queryKey: ['members', cid], queryFn: () => api.members(cid) });
  const invites = useQuery({ queryKey: ['invites', cid], queryFn: () => api.invites(cid) });
  const [created, setCreated] = useState<CreatedInviteDTO | null>(null);
  const [role, setRole] = useState<'player' | 'co-dm'>('player');
  const [label, setLabel] = useState('');
  const [once, setOnce] = useState(true);
  const [days, setDays] = useState(7);
  const [error, setError] = useState<string | null>(null);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
    void qc.invalidateQueries({ queryKey: ['members', cid] });
    void qc.invalidateQueries({ queryKey: ['invites', cid] });
  };

  const create = (e: React.FormEvent) => {
    e.preventDefault();
    void run(async () => {
      setCreated(await api.createInvite(cid, { role, label, maxUses: once ? 1 : null, expiresInDays: days }));
      setLabel('');
    });
  };

  return (
    <section className="card members-settings">
      <h2>{t('members.title')}</h2>
      <p className="small muted">{t('members.intro')}</p>
      <table className="table">
        <tbody>
          {members.data?.map((m) => (
            <tr key={m.userId}>
              <td>
                {m.name} {m.userId === myId && <span className="muted small">{t('members.you')}</span>}
              </td>
              <td>
                <select
                  value={m.role}
                  aria-label={t('members.role')}
                  onChange={(e) => void run(() => api.updateMember(cid, m.userId, e.target.value as MemberDTO['role']))}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {tDyn(`role.${r}`)}
                    </option>
                  ))}
                </select>
              </td>
              <td className="num">
                {m.userId !== myId && (
                  <button
                    className="danger small-btn"
                    onClick={() => confirm(t('members.removeConfirm', { name: m.name })) && void run(() => api.removeMember(cid, m.userId))}
                  >
                    {t('members.remove')}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>{t('invites.title')}</h3>
      <p className="small muted">{t('invites.intro')}</p>
      {created && (
        <div className="invite-created">
          <p className="small">{t('invites.newLink')}</p>
          <CopyField value={`${location.origin}/invite/${created.token}`} />
        </div>
      )}
      {invites.data?.length === 0 && !created && <p className="muted small">{t('invites.none')}</p>}
      {invites.data && invites.data.length > 0 && (
        <table className="table">
          <tbody>
            {invites.data.map((i) => (
              <tr key={i.id}>
                <td>
                  {tDyn(`role.${i.role}`)}
                  {i.label && <span className="muted"> · {i.label}</span>}
                </td>
                <td className="small muted">
                  {i.maxUses === null ? t('invites.usedUnlimited', { uses: i.uses }) : t('invites.used', { uses: i.uses, max: i.maxUses })} ·{' '}
                  {t('invites.until', { date: new Date(i.expiresAt).toLocaleDateString('de-DE') })}
                </td>
                <td className="num">
                  <button className="small-btn" onClick={() => void run(() => api.deleteInvite(cid, i.id))}>
                    {t('invites.revoke')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form className="row-form" onSubmit={create}>
        <label>
          {t('members.role')}
          <select value={role} onChange={(e) => setRole(e.target.value as 'player' | 'co-dm')}>
            <option value="player">{t('role.player')}</option>
            <option value="co-dm">{t('role.co-dm')}</option>
          </select>
        </label>
        <label>
          {t('invites.label')}
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={100} />
        </label>
        <label>
          {t('invites.uses')}
          <select value={once ? '1' : 'n'} onChange={(e) => setOnce(e.target.value === '1')}>
            <option value="1">{t('invites.once')}</option>
            <option value="n">{t('invites.unlimited')}</option>
          </select>
        </label>
        <label>
          {t('invites.expires')}
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {[1, 7, 30].map((d) => (
              <option key={d} value={d}>
                {t('invites.days', { n: d })}
              </option>
            ))}
          </select>
        </label>
        <button className="primary">{t('invites.create')}</button>
      </form>
      {error && <div className="error">{error}</div>}
    </section>
  );
}
